import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Slider,
    Tooltip,
    IconButton,
} from "@mui/material";
import {
    Save as SaveIcon,
    Undo as UndoIcon,
    FormatSize as FontSizeIcon,
    WrapText as WrapTextIcon,
} from "@mui/icons-material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Editor, { OnMount, Monaco } from '@monaco-editor/react';
import type { editor, IPosition } from 'monaco-editor';
import { AppDispatch, RootState } from "../../configureStore";
import { updatePrompt, createPrompt, getPrompts, clearSelectedPrompt } from "../../redux/reducer/prompts";
import { PromptDetail } from "../../interface/prompts";
import { FormTypeIdentifier } from "../../interface/extracted/types";
import { useTheme } from "../../ThemeContext";
import { registerPromptLanguage, PROMPT_LANGUAGE_ID } from "../../utils/monacoPromptLanguage";
import Button from "../atoms/Button";

// Form type options for extractor prompts
const FORM_TYPE_OPTIONS: FormTypeIdentifier[] = [
    '1040',
    '1040-nr',
    '1040-sr',
    '1040-x',
    '1065',
    '1120',
    '1120s',
    '1125-e',
    '4562',
    '4684',
    '4868',
    '8582',
    '8825',
    'schedule_1',
    'schedule_b',
    'schedule_c',
    'schedule_d',
    'schedule_e',
    'schedule_f',
    'schedule_k-1_1065',
    'schedule_k-1_1120s',
    'schedule_m-3_1065',
    'schedule_m-3_1120',
    'schedule_m-3_1120s',
    'w-2',
    'schedule_k-1_1041',
    'statements',
];

// Year options for year-specific extractors
const YEAR_OPTIONS = [2021, 2022, 2023, 2024, 2025];

interface PromptEditorProps {
    isCreating: boolean;
    isDuplicating: boolean;
    sourceContent: string;
    onCancelCreate: () => void;
    onContentChange: (content: string) => void;
}

interface CursorPosition {
    lineNumber: number;
    column: number;
}

const PromptEditor = ({
    isCreating,
    isDuplicating,
    sourceContent,
    onCancelCreate,
    onContentChange,
}: PromptEditorProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { mode } = useTheme();
    const selectedPrompt = useSelector<RootState, PromptDetail | null>(({ prompts }) => prompts.selectedPrompt);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['prompts/GET_PROMPT_CONTENT']);
    const updateLoading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['prompts/UPDATE_PROMPT']);
    const createLoading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['prompts/CREATE_PROMPT']);

    const [content, setContent] = useState<string>("");
    const [newPromptName, setNewPromptName] = useState<string>("");
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
    const [discardDialogOpen, setDiscardDialogOpen] = useState<boolean>(false);

    // Monaco editor state
    const [fontSize, setFontSize] = useState<number>(14);
    const [wordWrap, setWordWrap] = useState<boolean>(true);
    const [cursorPosition, setCursorPosition] = useState<CursorPosition>({ lineNumber: 1, column: 1 });
    const [lineCount, setLineCount] = useState<number>(1);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const monacoRef = useRef<Monaco | null>(null);

    // Extractor naming convention state
    const [isExtractorPrompt, setIsExtractorPrompt] = useState<boolean>(false);
    const [selectedFormType, setSelectedFormType] = useState<FormTypeIdentifier | ''>('');
    const [isYearSpecific, setIsYearSpecific] = useState<boolean>(false);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

    // Store handleSave in a ref so Monaco can access latest version
    const handleSaveRef = useRef<() => void>(() => {});

    // Track content in a ref to avoid controlled component cursor issues
    const contentRef = useRef<string>(content);

    // Compute initial content for editor based on current state (synchronous)
    const getInitialEditorContent = (): string => {
        if (isCreating) {
            return isDuplicating && sourceContent ? sourceContent : "";
        }
        return selectedPrompt?.content ?? "";
    };

    useEffect(() => {
        if (selectedPrompt) {
            setContent(selectedPrompt.content);
            contentRef.current = selectedPrompt.content;
            onContentChange(selectedPrompt.content);
            setHasChanges(false);
            // Update editor imperatively to avoid cursor issues
            if (editorRef.current && editorRef.current.getValue() !== selectedPrompt.content) {
                editorRef.current.setValue(selectedPrompt.content);
            }
        }
    }, [selectedPrompt]);

    useEffect(() => {
        if (isCreating) {
            const newContent = isDuplicating && sourceContent ? sourceContent : "";
            setContent(newContent);
            contentRef.current = newContent;
            onContentChange(newContent);
            setHasChanges(isDuplicating && !!sourceContent);
            setNewPromptName("");
            setIsExtractorPrompt(false);
            setSelectedFormType('');
            setIsYearSpecific(false);
            setSelectedYear(new Date().getFullYear());
            dispatch(clearSelectedPrompt());
            // Update editor imperatively to avoid cursor issues
            if (editorRef.current && editorRef.current.getValue() !== newContent) {
                editorRef.current.setValue(newContent);
            }
        }
    }, [isCreating, isDuplicating, sourceContent, dispatch]);

    // Generate the extractor prompt name based on the naming convention
    const getExtractorPromptName = (): string => {
        if (!selectedFormType) return '';
        let name = `extractor/${selectedFormType}-extract-only`;
        if (isYearSpecific) {
            name += `-${selectedYear}`;
        }
        name += '.txt';
        return name;
    };

    // Get the final prompt name for creating
    const getFinalPromptName = (): string => {
        if (isExtractorPrompt) {
            return getExtractorPromptName();
        }
        if (!newPromptName.trim()) return '';
        return newPromptName.endsWith('.txt') ? newPromptName : `${newPromptName}.txt`;
    };

    const handleContentChange = useCallback((newContent: string | undefined) => {
        const value = newContent ?? "";
        contentRef.current = value;
        setContent(value);
        onContentChange(value);
        if (selectedPrompt) {
            setHasChanges(value !== selectedPrompt.content);
        } else if (isCreating) {
            setHasChanges(value.length > 0 || newPromptName.length > 0 || (isExtractorPrompt && !!selectedFormType));
        }
        setSaveSuccess(false);
    }, [selectedPrompt, isCreating, newPromptName, isExtractorPrompt, selectedFormType, onContentChange]);

    const handleSave = useCallback(() => {
        if (isCreating) {
            const name = getFinalPromptName();
            if (!name) return;
            dispatch(createPrompt({
                input: { name, content },
                onSuccess: () => {
                    setSaveSuccess(true);
                    setHasChanges(false);
                    onCancelCreate();
                    dispatch(getPrompts({}));
                },
            }));
        } else if (selectedPrompt) {
            dispatch(updatePrompt({
                input: {
                    promptName: selectedPrompt.name,
                    content,
                },
                onSuccess: () => {
                    setSaveSuccess(true);
                    setHasChanges(false);
                },
            }));
        }
    }, [isCreating, content, selectedPrompt, dispatch, onCancelCreate]);

    // Keep handleSaveRef updated
    useEffect(() => {
        handleSaveRef.current = handleSave;
    }, [handleSave]);

    const handleRevert = () => {
        if (hasChanges) {
            setDiscardDialogOpen(true);
        }
    };

    const handleConfirmRevert = () => {
        const newContent = selectedPrompt ? selectedPrompt.content : "";
        setContent(newContent);
        contentRef.current = newContent;
        if (!selectedPrompt) {
            setNewPromptName("");
        }
        setHasChanges(false);
        setDiscardDialogOpen(false);
        // Update editor imperatively to avoid cursor issues
        if (editorRef.current && editorRef.current.getValue() !== newContent) {
            editorRef.current.setValue(newContent);
        }
    };

    // Monaco editor mount handler
    const handleEditorDidMount: OnMount = (monacoEditor, monacoInstance) => {
        editorRef.current = monacoEditor;
        monacoRef.current = monacoInstance;

        // Register custom language and themes
        registerPromptLanguage(monacoInstance);

        // Add Ctrl/Cmd+S save shortcut
         
        monacoEditor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS, () => {
            handleSaveRef.current();
        });

        // Track cursor position
        monacoEditor.onDidChangeCursorPosition((e: { position: IPosition }) => {
            setCursorPosition({
                lineNumber: e.position.lineNumber,
                column: e.position.column,
            });
        });

        // Track line count
        const model = monacoEditor.getModel();
        if (model) {
            setLineCount(model.getLineCount());
            model.onDidChangeContent(() => {
                setLineCount(model.getLineCount());
            });
        }

        // Focus the editor
        monacoEditor.focus();
    };

    // Get Monaco theme based on app theme
    const getMonacoTheme = () => (mode === 'dark' ? 'prompt-dark' : 'prompt-light');

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!selectedPrompt && !isCreating) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">
                    Select a prompt to edit or create a new one
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
                <DialogTitle>Discard Changes?</DialogTitle>
                <DialogContent>
                    <Typography>
                        You have unsaved changes. Are you sure you want to discard them?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiscardDialogOpen(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmRevert} variant="contained" color="error">
                        Discard
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">
                        {isCreating
                            ? (isDuplicating ? 'Duplicate Prompt' : 'Create New Prompt')
                            : selectedPrompt?.name.split('/').pop()}
                    </Typography>
                    {hasChanges && (
                        <Chip label="Unsaved changes" color="warning" size="small" />
                    )}
                </Box>
                {selectedPrompt && !isCreating && (
                    <Typography variant="caption" color="text.secondary">
                        Last modified: {new Date(selectedPrompt.lastModified).toLocaleString()}
                        {selectedPrompt.versionId && ` | Version: ${selectedPrompt.versionId.substring(0, 20)}...`}
                    </Typography>
                )}
            </Box>

            {/* New Prompt Name Input */}
            {isCreating && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={isExtractorPrompt}
                                onChange={(e) => {
                                    setIsExtractorPrompt(e.target.checked);
                                    if (e.target.checked) {
                                        setNewPromptName('');
                                    } else {
                                        setSelectedFormType('');
                                    }
                                    setHasChanges(content.length > 0);
                                }}
                            />
                          )}
                        label="Extractor prompt (uses naming convention)"
                        sx={{ mb: 2 }}
                    />

                    {isExtractorPrompt ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Form Type</InputLabel>
                                <Select
                                    value={selectedFormType}
                                    label="Form Type"
                                    onChange={(e) => {
                                        setSelectedFormType(e.target.value as FormTypeIdentifier);
                                        setHasChanges(true);
                                    }}
                                >
                                    {FORM_TYPE_OPTIONS.map((form) => (
                                        <MenuItem key={form} value={form}>
                                            {form}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isYearSpecific}
                                            onChange={(e) => setIsYearSpecific(e.target.checked)}
                                        />
                                      )}
                                    label="Year-specific"
                                />
                                {isYearSpecific && (
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            value={selectedYear}
                                            label="Year"
                                            onChange={(e) => setSelectedYear(e.target.value as number)}
                                        >
                                            {YEAR_OPTIONS.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>

                            {selectedFormType && (
                                <Typography variant="body2" color="text.secondary">
                                    File will be saved as: <strong>{getExtractorPromptName()}</strong>
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <TextField
                            label="Prompt Name"
                            value={newPromptName}
                            onChange={(e) => {
                                setNewPromptName(e.target.value);
                                setHasChanges(e.target.value.length > 0 || content.length > 0);
                            }}
                            fullWidth
                            size="small"
                            helperText="Extension .txt will be added if not provided. Use folder/name format for subfolders."
                            required
                        />
                    )}
                </Box>
            )}

            {/* Editor Toolbar */}
            <Box sx={{
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'background.default',
            }}
            >
                {/* Font Size Control */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Font size">
                        <FontSizeIcon fontSize="small" color="action" />
                    </Tooltip>
                    <Slider
                        value={fontSize}
                        onChange={(_, value) => setFontSize(value as number)}
                        min={10}
                        max={24}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}px`}
                        sx={{ width: 100 }}
                        size="small"
                    />
                </Box>

                {/* Word Wrap Toggle */}
                <Tooltip title={wordWrap ? "Word wrap: On" : "Word wrap: Off"}>
                    <IconButton
                        size="small"
                        onClick={() => setWordWrap(!wordWrap)}
                        color={wordWrap ? "primary" : "default"}
                    >
                        <WrapTextIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Box sx={{ flex: 1 }} />

                {/* Keyboard Shortcuts Hint */}
                <Typography variant="caption" color="text.secondary">
                    Ctrl+S Save | Ctrl+F Find | Ctrl+H Replace | Ctrl+Z Undo | Ctrl+Y Redo
                </Typography>
            </Box>

            {/* Monaco Editor */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <Editor
                    key={selectedPrompt?.name ?? (isCreating ? 'creating' : 'none')}
                    height="100%"
                    language={PROMPT_LANGUAGE_ID}
                    theme={getMonacoTheme()}
                    defaultValue={getInitialEditorContent()}
                    onChange={handleContentChange}
                    onMount={handleEditorDidMount}
                    beforeMount={(monaco) => registerPromptLanguage(monaco)}
                    options={{
                        fontSize,
                        fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                        minimap: { enabled: false },
                        lineNumbers: 'on',
                        wordWrap: wordWrap ? 'on' : 'off',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        renderWhitespace: 'selection',
                        bracketPairColorization: { enabled: true },
                        cursorBlinking: 'smooth',
                        cursorSmoothCaretAnimation: 'on',
                        smoothScrolling: true,
                        padding: { top: 8, bottom: 8 },
                        folding: true,
                        foldingHighlight: true,
                        showFoldingControls: 'mouseover',
                        lineDecorationsWidth: 8,
                        renderLineHighlight: 'line',
                        selectionHighlight: true,
                        occurrencesHighlight: 'singleFile',
                        quickSuggestions: false,
                        suggestOnTriggerCharacters: false,
                        parameterHints: { enabled: false },
                    }}
                    loading={(
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                />
            </Box>

            {/* Status Bar */}
            <Box sx={{
                px: 2,
                py: 0.5,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'background.default',
                fontSize: '0.75rem',
            }}
            >
                <Typography variant="caption" color="text.secondary">
                    Ln {cursorPosition.lineNumber}, Col {cursorPosition.column}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    |
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {lineCount} lines
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    |
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {content.length} characters
                </Typography>
                {hasChanges && (
                    <>
                        <Typography variant="caption" color="text.secondary">
                            |
                        </Typography>
                        <Typography variant="caption" color="warning.main">
                            Modified
                        </Typography>
                    </>
                )}
            </Box>

            {/* Actions */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {isCreating ? 'Prompt created successfully!' : 'Prompt saved successfully!'}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        loading={updateLoading || createLoading}
                        disabled={isCreating ? !getFinalPromptName() || !content.trim() : !hasChanges}
                    >
                        {isCreating ? 'Create' : 'Save'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UndoIcon />}
                        onClick={isCreating ? onCancelCreate : handleRevert}
                        disabled={!hasChanges && !isCreating}
                    >
                        {isCreating ? 'Cancel' : 'Revert'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default PromptEditor;
